import React, { useEffect, useMemo, useState } from "react";
import { X, Loader2, ArrowLeft } from "lucide-react";
import toast from "react-hot-toast";

import { getProposalTemplates, createProposal } from "../api/prospectingReportsApi";
import SubjectAddressSearch from "./SubjectAddressSearch";
import { COLLINGS_BRAND } from "../collingsBrand";
import ProposalBlockRenderer from "./ProposalBlockRenderer";

const field =
  "w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#55c5d0]/40";
const labelCls = "block text-xs font-semibold text-gray-500 mb-1";

// Fill merge tags with friendly sample values so a template preview reads like a
// real proposal (as Proply's does), without needing a saved proposal.
const SAMPLE = {
  address: "1/70 Palm Beach Avenue, Palm Beach",
  suburb: "Palm Beach",
  recipient_name: "the owner",
  agent_name: "Your agent",
  agent_phone: "",
  agent_email: "",
};
const fillSample = (blocks) =>
  JSON.parse(
    JSON.stringify(blocks || []).replace(/\{\{\s*(\w+)\s*\}\}/g, (m, k) =>
      Object.prototype.hasOwnProperty.call(SAMPLE, k) ? SAMPLE[k] : ""
    )
  );

/** A Mac-style browser window frame around the live template preview. */
const MacWindow = ({ title, children }) => (
  <div className="rounded-xl overflow-hidden border border-gray-200 shadow-sm bg-white flex flex-col h-full">
    <div className="flex items-center gap-2 px-4 py-2.5 bg-gray-100 border-b border-gray-200">
      <span className="w-3 h-3 rounded-full bg-[#ff5f57]" />
      <span className="w-3 h-3 rounded-full bg-[#febc2e]" />
      <span className="w-3 h-3 rounded-full bg-[#28c840]" />
      <div className="ml-3 flex-1">
        <div className="mx-auto max-w-sm truncate rounded-md bg-white border border-gray-200 px-3 py-1 text-xs text-gray-400 text-center">
          {title}
        </div>
      </div>
    </div>
    <div className="flex-1 overflow-y-auto bg-white">{children}</div>
  </div>
);

/**
 * Start a new proposal, Proply-style: choose a template (Library vs Custom tabs)
 * with a live Mac-window preview, then fill the subject + recipient. The backend
 * prefills address/comparables from any linked property / CMA passed in `prefill`.
 */
const CreateProposalModal = ({ onClose, onCreated, prefill = {} }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("library"); // library (system) | custom
  const [selectedId, setSelectedId] = useState(null);
  const [step, setStep] = useState("choose"); // choose | details
  const [form, setForm] = useState({
    full_address: prefill.full_address || "",
    suburb: prefill.suburb || "",
    recipient_name: prefill.recipient_name || "",
    recipient_email: prefill.recipient_email || "",
  });
  const [saving, setSaving] = useState(false);
  // Record ids picked from the CRM address search (property / lead / contact).
  const [links, setLinks] = useState(null);

  useEffect(() => {
    getProposalTemplates()
      .then(({ data }) => setTemplates(data?.results || []))
      .catch(() => toast.error("Could not load templates."))
      .finally(() => setLoading(false));
  }, []);

  const byTab = useMemo(() => ({
    library: templates.filter((t) => t.scope === "system"),
    custom: templates.filter((t) => t.scope !== "system"),
  }), [templates]);

  const list = byTab[tab];
  useEffect(() => {
    if (list.length && !list.some((t) => t.id === selectedId)) setSelectedId(list[0].id);
  }, [tab, list, selectedId]);

  const selected = templates.find((t) => t.id === selectedId) || null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.full_address.trim()) { toast.error("Add the property address."); return; }
    setSaving(true);
    try {
      const linkIds = links ? {
        property_id: links.property_id || undefined, lead_id: links.lead_id || undefined,
        contact_id: links.contact_id || undefined,
      } : {};
      const { data } = await createProposal({ template: selectedId || undefined, ...prefill, ...linkIds, ...form });
      onCreated(data);
    } catch (e) {
      toast.error("Could not create the proposal.");
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-6xl h-[88vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-900">
            {step === "choose" ? "Choose a template" : "Proposal details"}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex-1 min-h-0 flex">
          {/* Left: template list with Library / Custom tabs */}
          <div className="w-72 border-r border-gray-100 flex flex-col">
            <div className="p-4">
              <div className="inline-flex rounded-lg bg-gray-100 p-1 text-sm font-semibold">
                {[["library", "Library"], ["custom", "Custom"]].map(([k, lbl]) => (
                  <button key={k} onClick={() => setTab(k)}
                    className={`px-4 py-1.5 rounded-md ${tab === k ? "bg-white shadow text-gray-900" : "text-gray-500"}`}>
                    {lbl}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-3">
              {loading && <div className="text-gray-400 flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin" /></div>}
              {!loading && !list.length && (
                <p className="text-sm text-gray-400 px-1 py-4">
                  {tab === "custom" ? "No custom templates yet. Duplicate a library template to customise it." : "No templates."}
                </p>
              )}
              {list.map((t) => (
                <button key={t.id} onClick={() => { setSelectedId(t.id); setStep("choose"); }}
                  className={`block w-full text-left rounded-xl overflow-hidden border transition ${
                    selectedId === t.id ? "border-[#0f8d9b] ring-2 ring-[#0f8d9b]/20" : "border-gray-200 hover:border-gray-300"}`}>
                  <div className="h-24 bg-cover bg-center flex items-end"
                    style={t.cover_image_url
                      ? { backgroundImage: `url(${t.cover_image_url})` }
                      : { background: `linear-gradient(135deg, ${t.theme_colour || "#1f2a44"}, #0b1220)` }}>
                    <span className="text-white text-xs font-semibold px-3 py-2 drop-shadow">{t.name}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Right: preview (choose) or details form */}
          <div className="flex-1 min-h-0 flex flex-col bg-[#f5f6f8]">
            {step === "choose" ? (
              <>
                <div className="flex-1 min-h-0 p-5">
                  {selected ? (
                    <MacWindow title="preview.collings.com.au/proposal">
                      <div className="pointer-events-none">
                        <ProposalBlockRenderer
                          blocks={fillSample(selected.blocks)}
                          theme={selected.theme_colour || "#1f2a44"}
                          branding={COLLINGS_BRAND}
                        />
                      </div>
                    </MacWindow>
                  ) : (
                    <div className="h-full flex items-center justify-center text-gray-400">Select a template</div>
                  )}
                </div>
                <div className="flex items-center justify-between gap-4 px-5 py-4 border-t border-gray-200 bg-white">
                  <div className="min-w-0">
                    <div className="font-semibold text-gray-900 truncate">{selected?.name}</div>
                    {selected?.description && <div className="text-sm text-gray-500 truncate">{selected.description}</div>}
                  </div>
                  <button onClick={() => setStep("details")} disabled={!selected}
                    className="shrink-0 px-5 py-2.5 rounded-lg bg-[#0f8d9b] text-white text-sm font-semibold disabled:opacity-50">
                    Use template
                  </button>
                </div>
              </>
            ) : (
              <div className="flex-1 overflow-y-auto p-6">
                <button onClick={() => setStep("choose")} className="inline-flex items-center gap-1 text-sm text-gray-500 mb-4 hover:text-gray-700">
                  <ArrowLeft className="w-4 h-4" /> Back to templates
                </button>
                <div className="max-w-lg bg-white rounded-xl border border-gray-100 p-5">
                  <p className="text-sm text-gray-500 mb-4">Using <span className="font-semibold text-gray-800">{selected?.name}</span></p>
                  <div className="mb-3">
                    <label className={labelCls}>Property address</label>
                    <SubjectAddressSearch className={field} value={form.full_address}
                      linked={links ? links.label : ""}
                      onChange={(v) => { set("full_address", v); if (links) setLinks(null); }}
                      onPick={(r) => {
                        setForm((f) => ({
                          ...f,
                          full_address: r.full_address || f.full_address,
                          suburb: r.suburb || f.suburb,
                          recipient_name: r.recipient_name || f.recipient_name,
                          recipient_email: r.recipient_email || f.recipient_email,
                        }));
                        setLinks(r);
                      }} />
                  </div>
                  <div className="mb-3">
                    <label className={labelCls}>Suburb</label>
                    <input className={field} value={form.suburb} onChange={(e) => set("suburb", e.target.value)} />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="mb-3">
                      <label className={labelCls}>Vendor name</label>
                      <input className={field} value={form.recipient_name} onChange={(e) => set("recipient_name", e.target.value)} />
                    </div>
                    <div className="mb-3">
                      <label className={labelCls}>Vendor email</label>
                      <input className={field} value={form.recipient_email} onChange={(e) => set("recipient_email", e.target.value)} />
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 pt-2">
                    <button onClick={onClose} className="px-4 py-2.5 rounded-lg border border-gray-200 text-sm font-semibold text-gray-700">Cancel</button>
                    <button onClick={submit} disabled={saving}
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#0f8d9b] text-white text-sm font-semibold disabled:opacity-50">
                      {saving && <Loader2 className="w-4 h-4 animate-spin" />} Create &amp; edit
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateProposalModal;
